import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import MergeIcon from "@mui/icons-material/Merge";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import Box from "@mui/joy/Box";
import Checkbox from "@mui/joy/Checkbox";
import IconButton from "@mui/joy/IconButton";
import Sheet from "@mui/joy/Sheet";
import Stack from "@mui/joy/Stack";
import Table from "@mui/joy/Table";
import Tooltip from "@mui/joy/Tooltip";
import Typography from "@mui/joy/Typography";
import React from "react";
import { Link as BundleLink } from "../../../models/bundle";

export interface DataTableColumn<T> {
  key: string;
  header: string;
  width?: string;
  minWidth?: string;
  render: (item: T) => React.ReactNode;
}

export interface DataTableAction<T> {
  icon: React.ReactNode;
  label: string;
  color?: "primary" | "neutral" | "danger" | "success" | "warning";
  variant?: "solid" | "outlined" | "soft" | "plain";
  onClick: (selectedItems: T[]) => void;
  disabled?: (selectedItems: T[]) => boolean;
  minSelection?: number;
}

// Server-side pagination props
export interface ServerPagination {
  totalItems: number;
  currentPageSize: number;
  links: BundleLink[];
  onPageChange: (url: string) => void;
}

export interface DataTableProps<T> {
  // Data
  data: T[];
  columns: DataTableColumn<T>[];
  getItemId: (item: T) => string;

  // Selection
  selectable?: boolean;
  selectedItems?: string[];
  onSelectionChange?: (selectedIds: string[]) => void;

  // Actions
  actions?: DataTableAction<T>[];

  // Row interaction
  onRowClick?: (item: T) => void;

  // Pagination - either client-side or server-side
  serverPagination?: ServerPagination; // If provided, uses server-side pagination
  pageSize?: number; // For client-side pagination only
  showPagination?: boolean;
  paginationThreshold?: number; // For client-side pagination only

  // Styling
  stickyHeader?: boolean;
  stickyFooter?: boolean;
  height?: string;

  // Empty state
  emptyState?: {
    icon?: React.ReactNode;
    title: string;
    description?: string;
    actions?: React.ReactNode;
  };

  // Accessibility
  ariaLabel?: string;
}

const defaultLabelDisplayedRows = ({
  from,
  to,
  count,
}: {
  from: number;
  to: number;
  count: number;
}) => {
  const totalText = count !== -1 ? count : `more than ${to}`;
  return `${from}–${to} of ${totalText}`;
};

function extractOffset(url: string): number {
  const match = url.match(/_offset=(\d+)/);
  return match ? parseInt(match[1], 10) : 0;
}

function calculateServerPaginationInfo(
  serverPagination: ServerPagination,
  pageSize: number,
  showPagination: boolean
) {
  const selfLink = serverPagination.links.find((link) => link.relation === "self");
  const previousLink = serverPagination.links.find((link) => link.relation === "previous");
  const nextLink = serverPagination.links.find((link) => link.relation === "next");

  const currentOffset = selfLink ? extractOffset(selfLink.url) : 0;
  const currentPageSize = serverPagination.currentPageSize || pageSize;
  const currentPageNum = Math.floor(currentOffset / currentPageSize) + 1;
  const totalPages = Math.ceil(serverPagination.totalItems / currentPageSize);

  return {
    shouldShow: showPagination && serverPagination.totalItems > currentPageSize,
    from: serverPagination.totalItems === 0 ? 0 : currentOffset + 1,
    to: Math.min(currentOffset + currentPageSize, serverPagination.totalItems),
    total: serverPagination.totalItems,
    canGoPrevious: !!previousLink,
    canGoNext: !!nextLink,
    currentPage: currentPageNum,
    totalPages,
    previousLink,
    nextLink,
  };
}

function calculateClientPaginationInfo(
  data: any[],
  clientPage: number,
  pageSize: number,
  showPagination: boolean,
  paginationThreshold: number
) {
  const shouldShow = showPagination && data.length > paginationThreshold;
  const totalPages = Math.ceil(data.length / pageSize);

  return {
    shouldShow,
    from: data.length === 0 ? 0 : clientPage * pageSize + 1,
    to: Math.min((clientPage + 1) * pageSize, data.length),
    total: data.length,
    canGoPrevious: clientPage > 0,
    canGoNext: clientPage < totalPages - 1,
    currentPage: clientPage + 1,
    totalPages,
  };
}

function createSelectionHandlers<T>(
  currentPageData: T[],
  getItemId: (item: T) => string,
  selectedItems: string[],
  onSelectionChange?: (selectedIds: string[]) => void
) {
  const handleSelectAll = (checked: boolean) => {
    if (!onSelectionChange) return;

    if (checked) {
      const allIds = currentPageData.map(getItemId);
      const newSelection = [...new Set([...selectedItems, ...allIds])];
      onSelectionChange(newSelection);
    } else {
      const currentPageIds = currentPageData.map(getItemId);
      const newSelection = selectedItems.filter((id) => !currentPageIds.includes(id));
      onSelectionChange(newSelection);
    }
  };

  const handleSelectItem = (itemId: string, checked: boolean) => {
    if (!onSelectionChange) return;

    if (checked) {
      onSelectionChange([...selectedItems, itemId]);
    } else {
      onSelectionChange(selectedItems.filter((id) => id !== itemId));
    }
  };

  return { handleSelectAll, handleSelectItem };
}

interface TableHeaderProps<T> {
  selectable: boolean;
  isIndeterminate: boolean;
  isAllCurrentPageSelected: boolean;
  selectedItems: string[];
  handleSelectAll: (checked: boolean) => void;
  columns: DataTableColumn<T>[];
  actions: DataTableAction<T>[];
  selectedItemsData: T[];
}

function renderTableHeader<T>({
  selectable,
  isIndeterminate,
  isAllCurrentPageSelected,
  selectedItems,
  handleSelectAll,
  columns,
  actions,
  selectedItemsData,
}: TableHeaderProps<T>) {
  return (
    <thead>
      <tr>
        {selectable && (
          <th style={{ width: 48, textAlign: "center", padding: "12px 6px" }}>
            <Checkbox
              size="sm"
              indeterminate={isIndeterminate}
              checked={isAllCurrentPageSelected}
              onChange={(event) => handleSelectAll(event.target.checked)}
              color={selectedItems.length > 0 ? "primary" : undefined}
              sx={{ verticalAlign: "text-bottom" }}
            />
          </th>
        )}
        {columns.map((column) => (
          <th
            key={column.key}
            style={{
              padding: "12px 6px",
              width: column.width,
            }}
          >
            {column.header}
          </th>
        ))}
        {actions.length > 0 && (
          <th style={{ width: `${45 * actions.length}px` }}>
            {selectedItems.length > 0 && (
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                {actions.map((action) => {
                  const isDisabled =
                    action.disabled?.(selectedItemsData) ||
                    (action.minSelection ? selectedItems.length < action.minSelection : false);

                  return (
                    <Tooltip key={action.label} title={action.label} variant="solid">
                      <IconButton
                        onClick={() => action.onClick(selectedItemsData)}
                        color={action.color || "neutral"}
                        variant={action.variant || "outlined"}
                        disabled={isDisabled}
                      >
                        {action.icon}
                      </IconButton>
                    </Tooltip>
                  );
                })}
              </Stack>
            )}
          </th>
        )}
      </tr>
    </thead>
  );
}

export default function UnifiedDataTable<T>({
  data,
  columns,
  getItemId,
  selectable = false,
  selectedItems = [],
  onSelectionChange,
  actions = [],
  onRowClick,
  serverPagination,
  pageSize = 50,
  showPagination = true,
  paginationThreshold = 50,
  stickyHeader = true,
  stickyFooter = true,
  height = "75vh",
  emptyState,
  ariaLabel = "data table",
}: DataTableProps<T>) {
  const [clientPage, setClientPage] = React.useState(0);

  // Determine pagination mode and data
  const isServerPagination = !!serverPagination;
  let currentPageData = data;

  if (!isServerPagination && showPagination && data.length > paginationThreshold) {
    currentPageData = data.slice(clientPage * pageSize, (clientPage + 1) * pageSize);
  }

  // Calculate pagination info
  const paginationInfo = isServerPagination
    ? calculateServerPaginationInfo(serverPagination, pageSize, showPagination)
    : calculateClientPaginationInfo(
        data,
        clientPage,
        pageSize,
        showPagination,
        paginationThreshold
      );

  // Selection handlers
  const { handleSelectAll, handleSelectItem } = createSelectionHandlers(
    currentPageData,
    getItemId,
    selectedItems,
    onSelectionChange
  );

  // Pagination handlers
  const handleChangePage = (direction: "previous" | "next") => {
    if (isServerPagination) {
      const serverInfo = paginationInfo as ReturnType<typeof calculateServerPaginationInfo>;
      if (direction === "previous" && serverInfo.previousLink) {
        serverPagination.onPageChange(serverInfo.previousLink.url);
      } else if (direction === "next" && serverInfo.nextLink) {
        serverPagination.onPageChange(serverInfo.nextLink.url);
      }
    } else if (direction === "previous") {
      setClientPage((prev) => Math.max(0, prev - 1));
    } else {
      setClientPage((prev) => Math.min(prev + 1, paginationInfo.totalPages - 1));
    }
  };

  // Calculate selection state for current page
  const currentPageIds = currentPageData.map(getItemId);
  const currentPageSelectedCount = currentPageIds.filter((id) => selectedItems.includes(id)).length;
  const isAllCurrentPageSelected =
    currentPageSelectedCount === currentPageIds.length && currentPageIds.length > 0;
  const isIndeterminate =
    currentPageSelectedCount > 0 && currentPageSelectedCount < currentPageIds.length;

  // Get selected items for actions
  const selectedItemsData = data.filter((item) => selectedItems.includes(getItemId(item)));

  // Empty state
  if (data.length === 0 && emptyState) {
    return (
      <Sheet
        variant="outlined"
        sx={{
          minHeight: "450px",
          borderRadius: "md",
          minWidth: "100%",
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
        }}
      >
        <Stack direction="column" gap={1} alignItems="center">
          {emptyState.icon || <PersonOffIcon sx={{ fontSize: 128 }} color="disabled" />}
          <Typography level="h2" color="neutral">
            {emptyState.title}
          </Typography>
          {emptyState.description && (
            <Typography level="body-md" color="neutral">
              {emptyState.description}
            </Typography>
          )}
          {emptyState.actions}
        </Stack>
      </Sheet>
    );
  }

  return (
    <Sheet
      variant="outlined"
      sx={{
        borderRadius: "sm",
        height: height,
        overflow: "auto",
      }}
    >
      <Table
        aria-label={ariaLabel}
        stickyHeader={stickyHeader}
        stickyFooter={stickyFooter}
        hoverRow={!!onRowClick}
        sx={{
          "--TableCell-headBackground": "var(--joy-palette-background-level1)",
          "--Table-headerUnderlineThickness": "1px",
          "--TableRow-hoverBackground": onRowClick
            ? "var(--joy-palette-background-level1)"
            : "transparent",
          "--TableCell-paddingY": "4px",
          "--TableCell-paddingX": "8px",
        }}
      >
        {renderTableHeader({
          selectable,
          isIndeterminate,
          isAllCurrentPageSelected,
          selectedItems,
          handleSelectAll,
          columns,
          actions,
          selectedItemsData,
        })}
        <tbody>
          {currentPageData.map((item) => {
            const itemId = getItemId(item);
            const isSelected = selectedItems.includes(itemId);

            return (
              <tr
                key={itemId}
                onClick={onRowClick ? () => onRowClick(item) : undefined}
                style={{
                  cursor: onRowClick ? "pointer" : "default",
                }}
              >
                {selectable && (
                  <td
                    style={{ textAlign: "center", width: 120, verticalAlign: "middle" }}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <Checkbox
                      size="sm"
                      checked={isSelected}
                      color={isSelected ? "primary" : undefined}
                      onChange={(event) => handleSelectItem(itemId, event.target.checked)}
                      slotProps={{ checkbox: { sx: { textAlign: "left" } } }}
                      sx={{ verticalAlign: "text-bottom" }}
                    />
                  </td>
                )}
                {columns.map((column) => (
                  <td
                    key={column.key}
                    style={{
                      verticalAlign: "middle",
                      width: column.width,
                      minWidth: column.minWidth,
                    }}
                  >
                    {column.render(item)}
                  </td>
                ))}
                {actions.length > 0 && <td></td>}
              </tr>
            );
          })}
        </tbody>
        {paginationInfo.shouldShow && (
          <tfoot>
            <tr>
              <td colSpan={columns.length + (selectable ? 1 : 0) + (actions.length > 0 ? 1 : 0)}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "right",
                    gap: 2,
                    p: 0.5,
                  }}
                >
                  <Typography sx={{ textAlign: "center", minWidth: 80 }}>
                    {defaultLabelDisplayedRows({
                      from: paginationInfo.from,
                      to: paginationInfo.to,
                      count: paginationInfo.total,
                    })}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <IconButton
                      size="sm"
                      color="neutral"
                      variant="outlined"
                      disabled={!paginationInfo.canGoPrevious}
                      onClick={() => handleChangePage("previous")}
                      sx={{ bgcolor: "background.surface" }}
                    >
                      <KeyboardArrowLeftIcon />
                    </IconButton>
                    <IconButton
                      size="sm"
                      color="neutral"
                      variant="outlined"
                      disabled={!paginationInfo.canGoNext}
                      onClick={() => handleChangePage("next")}
                      sx={{ bgcolor: "background.surface" }}
                    >
                      <KeyboardArrowRightIcon />
                    </IconButton>
                  </Box>
                </Box>
              </td>
            </tr>
          </tfoot>
        )}
      </Table>
    </Sheet>
  );
}

// Common action creators for convenience
export const createDeleteAction = <T,>(onDelete: (items: T[]) => void): DataTableAction<T> => ({
  icon: <DeleteOutlineIcon />,
  label: "Delete selected items",
  color: "danger",
  variant: "outlined",
  onClick: onDelete,
  minSelection: 1,
});

export const createMergeAction = <T,>(onMerge: (items: T[]) => void): DataTableAction<T> => ({
  icon: <MergeIcon />,
  label: "Merge duplicated items",
  color: "neutral",
  variant: "outlined",
  onClick: onMerge,
  minSelection: 2,
});
